import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

/**
 * Interface state that is not domain state: theme, the sidebar, and the toast queue.
 *
 * Kept out of the domain stores so that a test of the asset list does not carry a theme with it,
 * and so that "what does the interface look like right now" is answerable from one place.
 */

export type ThemePreference = 'light' | 'dark' | 'system'

const THEME_KEY = 'oui-observe.theme'

export type ToastKind = 'info' | 'success' | 'warning' | 'error'

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  detail?: string
  /** Milliseconds. Errors default to staying until dismissed. */
  timeout: number | null
}

const readStoredTheme = (): ThemePreference => {
  try {
    const stored = globalThis.localStorage?.getItem(THEME_KEY)
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
  } catch {
    return 'system'
  }
}

export const useUiStore = defineStore('ui', () => {
  const theme = ref<ThemePreference>(readStoredTheme())
  const sidebarOpen = ref(false)
  const toasts = ref<Toast[]>([])

  let nextToastId = 1

  /**
   * Apply the preference to the document.
   *
   * "System" removes the attribute entirely rather than resolving it to a value, so the CSS media
   * query stays in charge and the page follows the OS when it changes at dusk. Resolving it once
   * at load would freeze the choice until a reload.
   */
  const applyTheme = (): void => {
    const root = globalThis.document?.documentElement
    if (!root) return

    if (theme.value === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme.value)
  }

  const setTheme = (value: ThemePreference): void => {
    theme.value = value
    try {
      globalThis.localStorage?.setItem(THEME_KEY, value)
    } catch {
      /* a browser with storage disabled still gets the theme, just not next time */
    }
    applyTheme()
  }

  const cycleTheme = (): void => {
    const order: ThemePreference[] = ['system', 'light', 'dark']
    const index = order.indexOf(theme.value)
    setTheme(order[(index + 1) % order.length])
  }

  const toggleSidebar = (): void => {
    sidebarOpen.value = !sidebarOpen.value
  }

  const closeSidebar = (): void => {
    sidebarOpen.value = false
  }

  const dismissToast = (id: number): void => {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  }

  /**
   * Queue a toast.
   *
   * Errors have no timeout by default. A monitoring product that flashes a failure for four
   * seconds while somebody is looking at another window has not told them anything.
   */
  const toast = (input: {
    kind?: ToastKind
    title: string
    detail?: string
    timeout?: number | null
  }): number => {
    const kind = input.kind ?? 'info'
    const id = nextToastId++
    const timeout =
      input.timeout !== undefined ? input.timeout : kind === 'error' ? null : kind === 'warning' ? 9_000 : 5_000

    toasts.value = [...toasts.value, { id, kind, title: input.title, detail: input.detail, timeout }]

    if (timeout !== null) setTimeout(() => dismissToast(id), timeout)

    return id
  }

  const notifySuccess = (title: string, detail?: string): number =>
    toast({ kind: 'success', title, detail })

  const notifyError = (title: string, detail?: string): number =>
    toast({ kind: 'error', title, detail })

  const resolvedTheme = computed<'light' | 'dark'>(() => {
    if (theme.value !== 'system') return theme.value
    return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  watch(theme, applyTheme, { immediate: true })

  return {
    theme,
    resolvedTheme,
    sidebarOpen,
    toasts,
    applyTheme,
    setTheme,
    cycleTheme,
    toggleSidebar,
    closeSidebar,
    toast,
    notifySuccess,
    notifyError,
    dismissToast,
  }
})
